import { FormEvent, useEffect, useState } from 'react'
import { clientsApi, fakturoidApi } from '@/api/endpoints'
import type { Client, FakturoidSubject } from '@/api/types'
import { Button, Card, Field, Input, Modal } from '@/components/ui'
import { errorMessage } from '@/utils/errors'
import { formatDecimalHours } from '@/utils/format'

export default function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([])
  const [name, setName] = useState('')
  const [contact, setContact] = useState('')
  const [monthlyHours, setMonthlyHours] = useState('')
  const [pairing, setPairing] = useState<Client | null>(null)
  const [editing, setEditing] = useState<Client | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = () => clientsApi.list(true).then(setClients)
  useEffect(() => {
    load()
  }, [])

  const create = async (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    await clientsApi.create({ name, contact: contact || undefined, monthlyHours: parseHours(monthlyHours) })
    setName('')
    setContact('')
    setMonthlyHours('')
    await load()
  }

  const toggleArchive = async (c: Client) => {
    await (c.archived ? clientsApi.unarchive(c.id) : clientsApi.archive(c.id))
    await load()
  }

  const unpair = async (c: Client) => {
    setError(null)
    try {
      await clientsApi.unpairSubject(c.id)
      await load()
    } catch (err) {
      setError(errorMessage(err, 'Spárování se nepodařilo zrušit.'))
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Klienti</h1>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <p className="text-sm text-red-700">{error}</p>
        </Card>
      )}

      <Card>
        <form onSubmit={create} className="flex flex-wrap items-end gap-3">
          <div className="min-w-48 flex-1">
            <Field label="Název">
              <Input value={name} onChange={(e) => setName(e.target.value)} required />
            </Field>
          </div>
          <div className="min-w-48 flex-1">
            <Field label="Kontakt">
              <Input value={contact} onChange={(e) => setContact(e.target.value)} />
            </Field>
          </div>
          <div className="w-40">
            <Field label="Úvazek (h/měsíc)">
              <Input
                value={monthlyHours}
                onChange={(e) => setMonthlyHours(e.target.value)}
                inputMode="decimal"
                placeholder="160"
              />
            </Field>
          </div>
          <Button type="submit">Přidat klienta</Button>
        </form>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-400">
                <th className="py-2">Název</th>
                <th className="py-2">Kontakt</th>
                <th className="py-2">Úvazek</th>
                <th className="py-2">Fakturoid</th>
                <th className="py-2 text-right">Akce</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className={`border-t border-slate-100 ${c.archived ? 'text-slate-400' : ''}`}>
                  <td className="py-2 font-medium">
                    {c.name}
                    {c.archived && <span className="ml-2 text-xs">(archiv)</span>}
                  </td>
                  <td className="py-2">{c.contact}</td>
                  <td className="py-2 tabular-nums">
                    {c.monthlyHours != null ? (
                      formatDecimalHours(c.monthlyHours)
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="py-2">
                    {c.fakturoidSubjectId ? (
                      <span className="text-slate-600">
                        {c.fakturoidSubjectName ?? `#${c.fakturoidSubjectId}`}
                        <button
                          className="ml-2 text-xs text-slate-400 hover:text-red-500 hover:underline"
                          onClick={() => unpair(c)}
                          title="Zrušit spárování"
                        >
                          ✕
                        </button>
                      </span>
                    ) : (
                      <button className="text-xs text-amber-700 hover:underline" onClick={() => setPairing(c)}>
                        nespárováno — spárovat
                      </button>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <button className="mr-3 text-xs text-brand-600 hover:underline" onClick={() => setEditing(c)}>
                      upravit
                    </button>
                    {c.fakturoidSubjectId && (
                      <button className="mr-3 text-xs text-brand-600 hover:underline" onClick={() => setPairing(c)}>
                        změnit subjekt
                      </button>
                    )}
                    <button className="text-xs text-brand-600 hover:underline" onClick={() => toggleArchive(c)}>
                      {c.archived ? 'obnovit' : 'archivovat'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {pairing && (
        <PairSubjectModal
          client={pairing}
          onClose={() => setPairing(null)}
          onPaired={async () => {
            setPairing(null)
            await load()
          }}
        />
      )}

      {editing && (
        <EditClientModal
          client={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await load()
          }}
        />
      )}
    </div>
  )
}

/** "160" i "160,5"; prázdné pole = klient úvazek nesleduje (null). */
function parseHours(input: string): number | null {
  const value = Number(input.replace(',', '.').trim())
  return input.trim() === '' || !Number.isFinite(value) || value <= 0 ? null : value
}

function EditClientModal({
  client,
  onClose,
  onSaved,
}: {
  client: Client
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(client.name)
  const [contact, setContact] = useState(client.contact ?? '')
  const [monthlyHours, setMonthlyHours] = useState(client.monthlyHours != null ? String(client.monthlyHours) : '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await clientsApi.update(client.id, {
        name,
        contact: contact || undefined,
        note: client.note ?? undefined,
        monthlyHours: parseHours(monthlyHours),
      })
      onSaved()
    } catch (err) {
      setError(errorMessage(err, 'Klienta se nepodařilo uložit.'))
      setBusy(false)
    }
  }

  return (
    <Modal title={`Klient — ${client.name}`} onClose={onClose}>
      <form onSubmit={save} className="space-y-4">
        <Field label="Název">
          <Input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </Field>
        <Field label="Kontakt">
          <Input value={contact} onChange={(e) => setContact(e.target.value)} />
        </Field>
        <Field label="Úvazek (h/měsíc)">
          <Input
            value={monthlyHours}
            onChange={(e) => setMonthlyHours(e.target.value)}
            inputMode="decimal"
            placeholder="160"
          />
        </Field>
        <p className="text-xs text-slate-400">
          Úvazek se ukazuje na Dnešku — kolik hodin do konce týdne a do měsíce chybí. Prázdné pole ukazatel vypne.
          Na vyúčtování a fakturaci nemá vliv.
        </p>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Zrušit
          </Button>
          <Button type="submit" disabled={busy}>
            Uložit
          </Button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * Výběr subjektu ve Fakturoidu. Schválně se vybírá ze seznamu podle jména
 * a IČO — opisování ID z adresy by nepohlídalo, že se trefíš do správného
 * kontaktu a ne do podobně pojmenovaného duplikátu.
 */
function PairSubjectModal({
  client,
  onClose,
  onPaired,
}: {
  client: Client
  onClose: () => void
  onPaired: () => void
}) {
  const [query, setQuery] = useState(client.name)
  const [subjects, setSubjects] = useState<FakturoidSubject[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Necháme uživatele dopsat, ať nemlátíme Fakturoid dotazem na každou klávesu.
  useEffect(() => {
    let current = true
    setLoading(true)
    const timer = setTimeout(() => {
      fakturoidApi
        .subjects(query)
        .then((data) => current && setSubjects(data))
        .catch((err) => current && setError(errorMessage(err, 'Subjekty se nepodařilo načíst.')))
        .finally(() => current && setLoading(false))
    }, 300)
    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [query])

  const pair = async (subject: FakturoidSubject) => {
    setBusy(true)
    setError(null)
    try {
      await clientsApi.pairSubject(client.id, subject.id, subject.name)
      onPaired()
    } catch (err) {
      setError(errorMessage(err, 'Spárování se nepovedlo.'))
      setBusy(false)
    }
  }

  const createNew = async () => {
    setBusy(true)
    setError(null)
    try {
      await clientsApi.createSubject(client.id)
      onPaired()
    } catch (err) {
      setError(errorMessage(err, 'Subjekt se nepodařilo založit.'))
      setBusy(false)
    }
  }

  return (
    <Modal title={`Fakturoid — ${client.name}`} onClose={onClose}>
      <div className="space-y-4">
        <Field label="Hledat subjekt">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Název nebo IČO" autoFocus />
        </Field>

        <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
          {loading && <p className="p-3 text-sm text-slate-400">Načítám…</p>}
          {!loading && subjects.length === 0 && (
            <p className="p-3 text-sm text-slate-400">Nic nenalezeno. Zkus jiný název nebo založ nový subjekt.</p>
          )}
          {!loading &&
            subjects.map((s) => (
              <button
                key={s.id}
                type="button"
                disabled={busy}
                onClick={() => pair(s)}
                className={`flex w-full items-baseline justify-between border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-slate-50 disabled:opacity-50 ${
                  s.id === client.fakturoidSubjectId ? 'bg-brand-50' : ''
                }`}
              >
                <span className="font-medium">{s.name ?? `#${s.id}`}</span>
                <span className="ml-3 shrink-0 text-xs text-slate-400">
                  {[s.registrationNo && `IČO ${s.registrationNo}`, s.city].filter(Boolean).join(' · ')}
                </span>
              </button>
            ))}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex items-center justify-between gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={createNew} disabled={busy}>
            Založit nový ve Fakturoidu
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Zavřít
          </Button>
        </div>
      </div>
    </Modal>
  )
}
